import React, { useCallback, useEffect, useState } from 'react'
import modalStyles from '../../styles/backyard/common/Modal.module.sass'
import styles from '../../styles/front/applicants/ExitIntentPopup.module.sass'
import ModalWrapper from '../common/Modal'

// gtag用の拡張型定義
declare global {
  interface Window {
    gtag: (event: string, action: string, params: {}) => void
  }
}

type Props = {
  title: string
  text: string
  imageUrl: string
  imageAlt: string
  appElementName: string
  isPreviewOpen?: boolean
  previewClose?: () => void
}

// ポップアップ非表示期間
const POPUP_DISPLAY_INTERVAL_DAY = 7
const enableBrowserBackInterception = false

// ローカルストレージを使って、ポップアップを表示するかどうかを管理
const isPopupDisplayable = (isPreviewOpen: boolean) => {
  if (isPreviewOpen) {
    return true
  }
  const lastPopupDisplayDate = localStorage.getItem('lastPopupDisplayDate')
  const now = new Date()
  // 7日以上経過していたらポップアップを表示
  if (lastPopupDisplayDate) {
    const lastShown = new Date(lastPopupDisplayDate)
    const diffDays = Math.floor((now.getTime() - lastShown.getTime()) / (1000 * 60 * 60 * 24))

    if (diffDays >= POPUP_DISPLAY_INTERVAL_DAY) {
      return true
    } else {
      return false
    }
  }
  return true
}

const ExitIntentPopup = ({ title, text, imageUrl, imageAlt, isPreviewOpen, previewClose, appElementName }: Props) => {
  const [popupEnabled, setPopupEnabled] = useState(isPopupDisplayable(isPreviewOpen || false))
  const [isShow, setIsShow] = useState(isPreviewOpen || false)
  const [nextUrl, setNextUrl] = useState(null)

  // 別ページへのリックをクリックした時の処理
  const handleLinkClick = useCallback(
    (e) => {
      const target = e.target.closest('a')
      if (target && target.tagName === 'A') {
        const href = target.getAttribute('href')
        if (href && popupEnabled && target.target !== '_blank' && !isAnchorLink(href)) {
          e.preventDefault() // ページ遷移を一時的に防ぐ
          setNextUrl(href) // 次のURLを保存
          displayPopup() // ポップアップを表示
        }
      }
    },
    [popupEnabled]
  )

  const isAnchorLink = (href) => {
    return href.startsWith('#')
  }

  const addState = useCallback(() => {
    if (!enableBrowserBackInterception || !popupEnabled) {
      return
    }
    const beforePage = window.history.state
    if (beforePage && beforePage.page === 'current') {
      return
    } else {
      window.history.pushState({ page: 'current' }, '', window.location.href)
    }
  }, [popupEnabled])

  // クリックイベントを追加。ユーザーアクションに関するイベントにかぎり、popstateが発火する。
  // スクロールやマウスオーバーなどのイベントは発火しないのでつけない。
  const addActionEventListener = () => {
    window.addEventListener('click', addState)
    window.addEventListener('touchend', addState)
  }

  const removeActionEventListener = () => {
    window.removeEventListener('touchend', addState)
    window.removeEventListener('click', addState)
  }

  const handlePopState = (event) => {
    const hash = window.location.hash
    // ハッシュが存在する場合はページ内アンカーリンクへの遷移とみなす
    if (hash) {
      // アンカーリンクへの遷移の場合は何もしない
      return
    }
    displayPopup()
  }

  const displayPopup = () => {
    if (popupEnabled) {
      gtagEvent('imp_exit_prevention_popup')
      setIsShow(true)
      setPopupEnabled(false)
      updateLastPopupDisplayDate()
    }
  }

  const updateLastPopupDisplayDate = () => {
    localStorage.setItem('lastPopupDisplayDate', new Date().toISOString())
  }

  useEffect(() => {
    if (enableBrowserBackInterception) {
      addActionEventListener()
      window.addEventListener('popstate', handlePopState)
    }
    document.addEventListener('click', handleLinkClick)

    return () => {
      if (enableBrowserBackInterception) {
        removeActionEventListener()
        window.removeEventListener('popstate', handlePopState)
      }
      document.removeEventListener('click', handleLinkClick)
    }
  }, [addState])

  // Google Analyticsのイベントを送信
  const gtagEvent = (eventName: string) => {
    if (window.gtag) {
      window.gtag('event', eventName, {})
    }
  }

  const inputContinue = () => {
    if (isPreviewOpen) {
      previewClose()
      return
    }
    gtagEvent('click_popup_continue_input')
    setPopupEnabled(false)
    setIsShow(false)
  }

  const leavePage = () => {
    if (isPreviewOpen) {
      previewClose()
      return
    }
    setPopupEnabled(false)
    setIsShow(false)
    gtagEvent('click_popup_exit_page')
    // form要素に属性を追加
    const form = document.querySelector('form')
    if (form) {
      form.setAttribute('data-popup-state', 'enabled')
    }
    // 履歴を戻す（ユーザーが「戻る」を選択した場合）
    if (nextUrl) {
      // nextUrlがある場合は、そのURLに遷移する
      window.location.href = nextUrl
    } else if (enableBrowserBackInterception) {
      // nextUrlがない場合は、履歴を戻す
      history.back()
    }
  }

  // 画像をプリロードする
  const preloadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.src = src
      img.onload = resolve
      img.onerror = reject
    })
  }

  useEffect(() => {
    if (!isPreviewOpen) {
      preloadImage(imageUrl).then(() => {
        console.log('Image preloaded')
      })
    }
  }, [])

  return (
    <ModalWrapper
      id="exit_intent_popup"
      isOpen={isShow}
      appElementName={appElementName}
      className={`${styles.popupWrapper}`}
      customOverlayClassName={modalStyles.fadeIn}
    >
      <div className={styles.exitIntentPopup}>
        <div className={styles.image}>
          <img src={imageUrl} alt={imageAlt || ''} width="425" height="340" />
        </div>
        <div className={styles.content}>
          <div className={styles.title} dangerouslySetInnerHTML={{ __html: title.replace(/\n/g, '<br>') }}></div>
          <div className={styles.text} dangerouslySetInnerHTML={{ __html: text.replace(/\n/g, '<br>') }}></div>
          <div className={styles.buttons}>
            <button className={`${styles.button} ${styles.continue}`} onClick={inputContinue}>
              入力を続ける
            </button>
            <button className={`${styles.button} ${styles.back}`} onClick={leavePage}>
              ページを離れる
            </button>
          </div>
        </div>
      </div>
    </ModalWrapper>
  )
}

export default ExitIntentPopup
